"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, MessageSquare, PhoneCall, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const credentials = [{
  label: "NASC Accredited"
}, {
  label: "CHAS Approved"
}, {
  label: "20+ Years Experience"
}];
export default function Cta() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="call-to-action" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden border-t border-border py-20 lg:py-28">
      {/* Structural Datum Rule Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-primary/20">
        <div className="h-full w-24 bg-primary mx-auto md:mx-0 md:ml-12" />
      </div>

      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-14 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="rounded-2xl border border-border/80 bg-secondary/90 p-8 sm:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
          {/* Subtle industrial structural grid background decoration */}
          <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(to_right,hsl(var(--secondary-foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--secondary-foreground))_1px,transparent_1px)] bg-[size:4rem_4rem]" aria-hidden="true" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-6">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 rounded-md bg-background/40 border border-border px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                <ShieldCheck className="size-4 text-primary" />
                <span>Safety-led commercial & residential access</span>
              </motion.div>

              <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground">
                Ready to discuss your access requirements?
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base sm:text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
                Speak directly with our team for site-ready access plans and reliable quotes across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
              </motion.p>

              {/* Verified Accreditations Row */}
              <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-2">
                {credentials.map((cred, i) => <div key={cred.label} data-index={i} className="flex items-center gap-2 text-xs font-medium text-secondary-foreground/90 bg-background/30 rounded-md p-2 border border-border/50">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span className="truncate">{cred.label}</span>
                  </div>)}
              </motion.div>
            </div>

            {/* Right Action Column */}
            <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col gap-4 bg-background/50 backdrop-blur-xs p-6 sm:p-8 rounded-xl border border-border shadow-lg">
              <div className="space-y-1 pb-2 border-b border-border/70">
                <span className="text-xs uppercase tracking-wider font-semibold text-secondary-foreground/70">Direct Contact</span>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-secondary-foreground/80 font-medium">South East Operations</span>
                  <span className="text-xs text-secondary-foreground/70">Mon - Fri, 8am - 6pm</span>
                </div>
              </div>

              {/* Signature Three-Button Action Group */}
              <div className="flex flex-col gap-3 pt-1">
                {/* 1. Request a Quote (Primary Amber) */}
                <Button asChild size="lg" className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-md transition-colors">
                  <Link to="/ContactRequestQuotePage" className="flex items-center justify-center gap-2">
                    <span>Request a Quote</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>

                {/* 2. Call Now (Secondary Button with Phone Display) */}
                <Button asChild variant="secondary" size="lg" className="w-full h-12 bg-card hover:bg-card/90 text-card-foreground border border-border font-semibold shadow-xs transition-colors">
                  <a href="tel:01344929900" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4 text-primary" />
                    <span>Call 01344 929900</span>
                  </a>
                </Button>

                {/* 3. Discuss a Project (Outline Button) */}
                <Button asChild variant="outline" size="lg" className="w-full h-12 border-border/80 text-secondary-foreground hover:bg-background/80 hover:text-secondary-foreground font-medium transition-colors">
                  <Link to="/ContactRequestQuotePage" className="flex items-center justify-center gap-2">
                    <MessageSquare className="size-4" />
                    <span>Discuss a Project</span>
                  </Link>
                </Button>
              </div>

              <p className="text-center text-xs text-secondary-foreground/70 pt-1">
                Direct accountability • No obligation consultations • Fast turnaround
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}